"use client"

import * as SwitchPrimitive from "@radix-ui/react-switch"
import { type ComponentPropsWithoutRef, type ComponentRef, forwardRef } from "react"

/**
 * Switch primitive using inline styles only.
 *
 * Host apps (apps/app, apps/web) run Tailwind v4 without an `@source`
 * directive pointing at `packages/consent/**`, so any utility class we
 * reference here that isn't ALSO used in the host app's own source files
 * silently fails to emit (collapses to `initial`). That previously left
 * the thumb at 0x0 dimensions, no translate, and no background-colour.
 *
 * Applying styles inline sidesteps Tailwind's content scan entirely and
 * guarantees the control renders correctly regardless of how the consumer
 * configures Tailwind. Colours resolve against the host app's `@theme`
 * tokens via `var(--color-*)` with sensible fallbacks.
 */
export const Switch = forwardRef<
  ComponentRef<typeof SwitchPrimitive.Root>,
  ComponentPropsWithoutRef<typeof SwitchPrimitive.Root>
>(({ checked, defaultChecked, disabled, style, ...props }, ref) => {
  const isChecked = (checked ?? defaultChecked ?? false) === true

  return (
    <SwitchPrimitive.Root
      ref={ref}
      checked={checked}
      defaultChecked={defaultChecked}
      disabled={disabled}
      style={{
        display: "inline-flex",
        flexShrink: 0,
        alignItems: "center",
        width: 28,
        height: 16,
        borderRadius: 9999,
        border: "2px solid transparent",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        transition: "background-color 200ms",
        backgroundColor: isChecked
          ? "var(--color-primary, hsl(234 98% 61%))"
          : "var(--color-input, hsl(0 0% 88%))",
        ...style,
      }}
      {...props}
    >
      <SwitchPrimitive.Thumb
        style={{
          display: "block",
          width: 12,
          height: 12,
          borderRadius: "50%",
          backgroundColor: "#fff",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.25)",
          transition: "transform 200ms",
          transform: isChecked ? "translateX(12px)" : "translateX(0)",
          pointerEvents: "none",
        }}
      />
    </SwitchPrimitive.Root>
  )
})
Switch.displayName = "Switch"
